<template>
  <div
    v-if="loading"
    class="gift-loading"
  >
    <c-loader-spinner :size="1.5" />
  </div>

  <div v-else>
    <div
      ref="container"
      class="gift-container"
      tabindex="-1"
    >
      <template v-if="selectedGiftCard">
        <div class="gift-card-single">
          <div class="gift-card">
            <div class="gift-card__button is-active">
              <h3 class="gift-card__title">
                {{ giftcardTitle(selectedGiftCard) }}
              </h3>
              <img
                class="gift-card__image"
                :src="imageSrc(selectedGiftCard)"
                width="320"
                height="190"
                :alt="giftcardTitle(selectedGiftCard)"
              >
            </div>

            <button
              class="gift-card__info"
              @click="displayInfo(selectedGiftCard)"
            >
              <c-icon
                type="info"
                :size="1.5"
              />
            </button>
          </div>

          <div class="gift-note">
            <h3>{{ $t('other_header') }}</h3>
            <div v-html="$t('other_text')" />

            <c-action
              color="secondary-inverse-dark"
              @click="selectGiftCard(null)"
            >
              <c-icon
                type="chevron-left"
                :size="1"
              />
              {{ $t('other_link') }}
            </c-action>
          </div>
        </div>

        <div class="gift-prices">
          <div
            v-if="loadingPrices"
            class="gift-loading"
          >
            <c-loader-spinner :size="1.5" />
          </div>

          <template
            v-for="priceId in selectedGiftCard.prices"
            v-else
          >
            <div
              v-if="ticketInfoAvailable(priceId)"
              :key="priceId"
              class="form-input form-input--inline-label form-input--neutral"
            >
              <select
                :id="'tickettype-' + priceId"
                v-model.number="selectedTickets[priceId]"
                class="form-input__input form-input__input--select"
                @change="updateSelection()"
              >
                <option
                  :value="0"
                  selected
                >
                  0
                </option>
                <option
                  v-for="n in maxTickets"
                  :key="n"
                  :value="n"
                >
                  {{ n }}
                </option>
              </select>
              <label
                :for="'tickettype-' + priceId"
                class="form-input__label"
              >
                {{ ticketTitle(priceId) }} {{ price(getPrice(priceId)) }} €
              </label>
            </div>
          </template>

          <div
            v-if="ticketSelectionWarning"
            class="selection__warning"
          >
            {{ $t('ticketSelectionWarning') }}
          </div>
        </div>

        <div
          v-if="showTemplateSelection"
          class="gift-template"
        >
          <div
            v-if="loadingTemplates"
            class="gift-loading"
          >
            <c-loader-spinner :size="1.5" />
          </div>

          <template v-else>
            <p><em>{{ $t('template_selection') }}</em></p>

            <div class="gift-template__list">
              <button
                v-for="template in giftCardTemplates"
                :key="template.id"
                class="gift-card__button has-background"
                :class="{
                  'is-active': (selectedTemplate && template.id === selectedTemplate.id)
                }"
                @click="selectTemplate(template)"
              >
                <h3 class="gift-card__title">
                  {{ templateTitle(template) }}
                </h3>
                <img
                  class="gift-card__image"
                  :src="imageSrc(template)"
                  width="300"
                  height="200"
                  :alt="templateTitle(template)"
                >
              </button>
            </div>
          </template>
        </div>
      </template>

      <template v-else>
        <p><em>{{ $t('selectGiftcard') }}</em></p>

        <div class="gift-card-list">
          <div
            v-for="giftCard in giftCards"
            :key="giftCard.id"
            class="gift-card"
          >
            <button
              class="gift-card__button"
              @click="selectGiftCard(giftCard)"
            >
              <h3 class="gift-card__title">
                {{ giftcardTitle(giftCard) }}
              </h3>
              <img
                class="gift-card__image"
                :src="imageSrc(giftCard)"
                width="300"
                height="178"
                :alt="giftcardTitle(giftCard)"
              >
            </button>
            <button
              class="gift-card__info"
              @click="displayInfo(giftCard)"
            >
              <c-icon
                type="info"
                :size="1.5"
              />
            </button>
          </div>
        </div>
      </template>
    </div>

    <c-banner
      v-if="selectedTemplate"
      color="primary"
      class="gift-checkout"
    >
      <c-action-link
        color="primary-inverse-dark"
        @click="addToBasket()"
      >
        {{ $t('add_to_basket') }}
        <c-icon
          type="chevron-right"
          :size="0.75"
        />
      </c-action-link>
    </c-banner>

    <c-modal
      v-if="showInfo"
      size="medium"
      @close="hideInfo"
    >
      <div v-html="infoText" />

      <template #footer>
        <c-action
          color="primary"
          @click="hideInfo"
        >
          Ok
        </c-action>
      </template>
    </c-modal>
  </div>
</template>

<script>
import CAction from './action.vue'
import CActionLink from './actionLink.vue'
import CBanner from './banner.vue'
import CIcon from './icon.vue'
import CLoaderSpinner from './loaderSpinner.vue'
import CModal from './modal.vue'

import priceHelper from '../functions/priceHelper.js'

import { BASE_URL, GAP_GIFT_CARD } from '../settings.js'

export default {
  name: 'CGiftCardsSelection',

  components: {
    CLoaderSpinner,
    CModal,
    CIcon,
    CAction,
    CActionLink,
    CBanner
  },

  data () {
    return {
      loading: false,
      showInfo: false,
      infoText: '',

      giftCards: {},
      selectedGiftCard: null,

      loadingPrices: false,
      selectedTickets: {},
      maxTickets: 14,

      ticketSelectionWarning: false,
      showTemplateSelection: false,

      loadingTemplates: true,
      giftCardTemplates: {},
      selectedTemplate: null
    }
  },

  created () {
    this.loadGiftCards()
    this.loadGiftCardTemplates()
  },

  methods: {
    resetGiftCardSelection () {
      this.selectedTickets = {}
      if (this.selectedGiftCard) {
        for (const pId in this.selectedGiftCard.prices) {
          this.selectedTickets[this.selectedGiftCard.prices[pId]] = 0
        }
      }
    },

    loadGiftCards () {
      this.loading = true

      this.$store.dispatch('loadGiftCards').then(response => {
        this.loading = false
        this.giftCards = response.data
        this.selectedGiftCard = this.$store.state.selectedGiftCard
        this.resetGiftCardSelection()

        this.selectedTickets = this.$store.state.selectedGiftCardTickets
        this.selectedTemplate = this.$store.state.selectedGiftCardTemplate

        this.updateSelection()
      })
    },

    loadGiftCardTemplates () {
      this.loadingTemplates = true

      this.$store.dispatch('loadGiftCardTemplates').then(response => {
        this.loadingTemplates = false
        this.giftCardTemplates = response.data
      })
    },

    giftcardTitle (giftcard) {
      return giftcard['title_' + this.$i18n.locale]
    },

    templateTitle (template) {
      return template['title_' + this.$i18n.locale]
    },

    displayInfo (giftCard) {
      this.infoText = giftCard['info_' + this.$i18n.locale]
      this.showInfo = true
    },

    hideInfo () {
      this.showInfo = false
    },

    selectGiftCard (giftCard) {
      this.selectedGiftCard = giftCard
      this.$store.commit('setSelectedGiftcard', giftCard)
      this.resetGiftCardSelection()
      this.$store.commit('setSelectedGiftcardTickets', this.selectedTickets)

      if (!giftCard) {
        this.showTemplateSelection = false
      }

      this.$refs.container.focus()
    },

    selectTemplate (template) {
      this.selectedTemplate = template
      this.$store.commit('setSelectedGiftcardTemplate', template)
    },

    imageSrc (giftCard) {
      const image = giftCard['image_' + this.$i18n.locale]
      return `${BASE_URL}static/img/${image}`
    },

    ticketInfoAvailable (ticketId) {
      return ticketId in this.$store.state.articles
    },

    getTicket (ticketId) {
      if (this.ticketInfoAvailable(ticketId)) {
        return this.$store.state.articles[ticketId]
      }

      return null
    },

    ticketTitle: function (ticketId) {
      return this.getTicket(ticketId)['text_' + this.$i18n.locale]
    },

    getPrice (ticketId) {
      const ticket = this.getTicket(ticketId)

      if (ticket) {
        return ticket.price
      }

      return 0
    },

    price (price) {
      return priceHelper.format((price / 100), this.$i18n.locale)
    },

    updateSelection () {
      let atLeastOne = false
      let sum = 0
      for (const tId in this.selectedTickets) {
        if (this.selectedTickets[tId] > 0) {
          sum += this.getPrice(tId)
          atLeastOne = true
        }
      }

      if (!atLeastOne || sum === 0) {
        this.selectTemplate(null)
      }

      this.$store.commit('setSelectedGiftcardTickets', this.selectedTickets)
      this.ticketSelectionWarning = (atLeastOne && sum === 0)
      this.showTemplateSelection = atLeastOne
    },

    addToBasket () {
      const payload = {
        type: this.selectedGiftCard.type,
        data: {
          gap: GAP_GIFT_CARD,
          date: '1970-01-01',
          articles: {}
        }
      }

      for (const tId in this.selectedTickets) {
        if (this.selectedTickets[tId] > 0) {
          payload.data.articles[tId] = { count: this.selectedTickets[tId] }
        }
      }

      this.$store.dispatch('addGiftCardsToCart', payload)
        .then(() => {
          const route = 'booking-overview-' + this.$i18n.locale
          this.$router.push({ name: route })
        })
    }
  }
}
</script>

<i18n locale="de">
{
  "selectGiftcard": "Wählen Sie hier aus unserem Geschenkgutscheine-Angebot für Print@Home.",
  "other_header": "Sie möchten doch einen anderen Geschenkgutschein buchen?",
  "other_text": "<p>Gehen sie einfach einen Schritt zurück und treffen Sie eine neue Auswahl.</p><p>Sie erhalten einen Gutschein für alle ausgewählten Tickets pro Bestellung. Wenn Sie verschiedene Gutscheine buchen wollen, müssen Sie diese separat kaufen.</p><p>Weitere Angebote wie z.B. eine Stadtrundfahrt können Sie im nächsten Schritt dazu buchen.</p>",
  "other_link": "Zurück zur Gutschein Auswahl",
  "template_selection": "Wählen Sie ein Design für Ihren Print@Home Gutschein aus.",
  "add_to_basket": "In den Warenkorb",
  "ticketSelectionWarning": "Bitte beachten: Kleinkinder brauchen eine Begleitperson."
}
</i18n>

<i18n locale="en">
{
  "selectGiftcard": "Here you can choose from our gift voucher offers as print@Home.",
  "other_header": "You want to purchase a different gift voucher?",
  "other_text": "<p>Simply go back and make a new selection.</p><p>You will receive one voucher for all tickets per order. If you want to book various gift vouchers, you have to order these separately.</p><p>Other recommendations, like i.e. a city tour, can be booked in the next step.</p>",
  "other_link": "Back to gift voucher selection",
  "template_selection": "Select a design for your print@Home gift voucher. If you do not make a selection our default design will be used.",
  "add_to_basket": "Add to basket",
  "ticketSelectionWarning": "Please note: Infants need a companion."
}
</i18n>

<style lang="scss">
@import "../css/variables";

.gift-container {
  margin: 0 1rem;
  padding-bottom: 1rem;

  outline: none;

  @media (min-width: $breakpoint-md) {
    margin: 0 2rem;
    padding-bottom: 2rem;
  }
}

.gift-card-single {
  display: grid;
  gap: 1em;
  justify-items: start;

  margin-bottom: 1em;

  @media (min-width: $breakpoint-lg) {
    grid-template-columns: max-content 1fr;
    align-items: end;
  }
}

.gift-card-list {
  display: flex;
  flex-wrap: wrap;

  margin: 0 -0.5em;
}

.gift-card {
  position: relative;
}

.gift-card__button {
  padding: 0.5em;

  text-align: left;
  color: $color-secondary-dark;

  background: none;
  border: 0.125em solid transparent;
  border-radius: 1em;

  &.has-background {
    background: $color-background;
  }

  &:hover,
  &:focus,
  &.is-active {
    color: #fff;

    background: $color-secondary;
    border-color: $color-secondary-dark;
    outline: none;
  }
}

.gift-card__title {
  margin-top: 0.125em;
  margin-bottom: 0.75em;
}

.gift-card__image {
  vertical-align: middle;
  border-radius: 0.625em;
}

.gift-card__info {
  position: absolute;
  top: 0.5em;
  right: 0.5em;

  padding: 0;

  color: $color-secondary-dark;

  background: none;
  border: 0;

  &:hover,
  &:focus {
    color: $color-primary;
  }

  .gift-card__button.is-active + & {
    color: #fff;
  }
}

.gift-note {
  padding: 1em;

  background: $color-background;
  border: 0.125em solid $color-muted;
  border-radius: 1em;

  h3 {
    font-size: 1.125em;
    margin-bottom: 0.5em;
  }

  p {
    margin-bottom: 0.5em;
  }
}

.gift-prices {
  display: grid;
  gap: 0.5em;
}

.gift-template {
  margin-top: 1.5em;
  padding-top: 1.5em;

  border-top: $border-dashed;
}

.gift-template__list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
  gap: 0.5em;
}

.gift-checkout {
  text-align: center;
}
</style>
